import React, { useState, useEffect } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  DateInput,
  ReferenceInput,
  AutocompleteInput,
  FormDataConsumer,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import { FormToolbar } from '../../common/FormToolbar';
import { OrderItemsGrid } from './OrderItemsGrid';
import { TextField, CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useForm } from 'react-final-form';
import { CustomerFromMobile } from './CustomerFromMobile';

// Export function for order details
const exportOrderDetails = async (
  dataProvider: any, 
  orderId: string, 
  orderNumber: string, 
  orderDate: string, 
  items: any[], 
  designCode: string | null,
  customerName: string,
  mobileNo: string
) => {
  try {
    // Get product details for all items
    const productIds = items.map(item => item.productId);
    const { data: products } = await dataProvider.getList('products', {
      filter: { id: { in: productIds } },
      pagination: { page: 1, perPage: 5000 },
      sort: { field: 'id', order: 'ASC' },
    });
    
    // Create product map
    const productsMap = products.reduce((map: any, product: any) => {
      map[product.id] = product;
      return map;
    }, {});
    
    // Prepare CSV data with Customer Name and Mobile No
    const rows = [
      ['Order No.', orderNumber, 'Order Date', orderDate],
      ['Customer Name', customerName || ''],
      ['Mobile No.', mobileNo || ''],
      ['Design Code', designCode || ''],
      [],
      ['Item Name', 'Quantity'],
      ...items.map(item => {
        const product = productsMap[item.productId];
        return [
          product?.productName || '',
          item.qty
        ];
      }),
      [],
      ['Total Items', String(items.length)],
      ['Total Quantity', String(items.reduce((sum, item) => sum + item.qty, 0))],
    ];
    
    // Convert to CSV
    const csv = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    // Download CSV
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Order_${orderNumber.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error exporting order details:', error);
  }
};

const DesignSelectWithPhoto = () => {
  const dataProvider = useDataProvider();
  const form = useForm();
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      try {
        const res = await dataProvider.getList('product-groups', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'designName', order: 'ASC' },
          filter: {},
        });
        setOptions(res.data || []);
      } catch (e) {
        console.error('Error loading product groups', e);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [dataProvider]);

  const currentDesignId = form.getState().values.designCode;
  const currentValue = options.find(o => o.id === currentDesignId) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: any) => option?.designName || option?.name || ''}
      value={currentValue}
      onChange={(_e, value: any) => {
        form.change('designCode', value ? value.id : undefined);
      }}
      renderOption={(option: any) => {
        const name = option?.designName || option?.name || '';
        const photo = option?.photograph;
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {photo ? (
              <img
                src={photo}
                alt={name}
                style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 8, borderRadius: 4, background: '#fff' }}
              />
            ) : null}
            <span>{name}</span>
          </span>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Design"
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export const OrderCreate = (props: any) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const location = useLocation();
  
  const [items, setItems] = useState<{ productId: number; qty: number }[]>([]);
  const [selectedDesignCode, setSelectedDesignCode] = useState<string | null>(null);
  const [isCustLogin, setIsCustLogin] = useState(false);

  const handleItemsChange = React.useCallback(
    (items) => {
        setItems(items);
    },
    []
  );

  useEffect(() => {
    const type = localStorage.getItem("type");
    setIsCustLogin(type === "isCustLogin");
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      
      const toCreate = items.filter((it) => it.qty > 0);
      if (toCreate.length === 0) {
        notify('Please add at least one item with quantity before saving', { type: 'warning' });
        return;
      }

      // Strip out UI-only fields so they are not sent to the orders endpoint
      const {
        customerMobile,
        newCustomerName,
        newCustomerCode,
        newCustomerState,
        newCustomerAddress1,
        newCustomerAddress2,
        newCustomerAddress3,
        newCustomerPincode,
        isNewCustomerFromMobile,
        ...cleanValues
      } = values;

      const designCode: string | undefined = selectedDesignCode || values.designCode;
      let orderNo: string | undefined = values.orderNo;

      // Ensure we have a customerId
      let customerId: number | undefined = values.customerId;
      if (!customerId && customerMobile && newCustomerName) {
        try {
          const createdCustomer: any = await dataProvider.create('customers', {
            data: {
              code: newCustomerCode || customerMobile,
              customerName: newCustomerName,
              mobileNo: customerMobile,
              state: newCustomerState,
              address1: newCustomerAddress1,
              address2: newCustomerAddress2,
              address3: newCustomerAddress3,
              pincode: newCustomerPincode,
            },
          });
          customerId = createdCustomer.data.id;
        } catch (e) {
          const anyErr: any = e;
          notify(anyErr?.message || 'Error creating customer from mobile', { type: 'warning' });
          return;
        }
      }

      if (!customerId) {
        notify('Please select a customer before creating the order', { type: 'warning' });
        return;
      }

      const header = {
        ...cleanValues,
        customerId,
        orderNo,
        orderDate: values.orderDate
          ? new Date(values.orderDate).toISOString()
          : new Date().toISOString(),
      };

      const created: any = await dataProvider.create('orders', { data: header });
      const orderId = created.data.id;
      
      // Fetch order details + customer info
      let orderNumber = '';
      let orderDate = '';
      let customerName = '';
      let mobileNo = '';

      try {
        const orderDetails: any = await dataProvider.getOne('orders', { id: orderId });
        orderNumber = orderDetails.data.orderNo || '';
        orderDate = orderDetails.data.orderDate || '';
        
        if (orderDetails.data.customerId) {
          const customerRes: any = await dataProvider.getOne('customers', { 
            id: orderDetails.data.customerId 
          });
          customerName = customerRes.data.customerName || '';
          mobileNo = customerRes.data.mobileNo || '';
        }
      } catch (e) {
        console.error('Failed to fetch order details:', e);
      }
      
      for (const it of toCreate) {
        await dataProvider.create('order-items', {
          data: {
            orderId,
            productId: it.productId,
            qty: it.qty,
          },
        });
      }

      // Export order details to CSV with customer info
      exportOrderDetails(
        dataProvider, 
        created.data.id, 
        orderNumber,
        orderDate, 
        toCreate, 
        selectedDesignCode,
        customerName,
        mobileNo
      );

      notify('Order created', { type: 'info' });
      
      if (isCustLogin) {
        alert(`Order ${orderNumber} created successfully`);
        redirect('list', '/orders');
      } else {
        redirect('list', '/orders');
      }
    } catch (e) {
      console.error(e);
      const anyErr: any = e;
      notify(anyErr?.message || 'Error creating order', { type: 'warning' });
    }
  };

  return (
    <React.Fragment>
      <Create title="Create Order" {...props}>
        <SimpleForm
          warnWhenUnsavedChanges
          toolbar={<FormToolbar onSave={handleSubmit} onClose={props.onClose} onCancel={props.onCancel} {...props} />}
          variant="outlined"
          save={handleSubmit}
        >
          <TextInput source="orderNo" label="Order No" fullWidth disabled />
          <DateInput source="orderDate" label="Order Date" fullWidth defaultValue={new Date().toISOString()} />
          <DesignSelectWithPhoto />

          <FormDataConsumer>
            {({ formData }) => (
              formData?.designCode ? (
                <DesignPreview id={formData.designCode} dataProvider={dataProvider} onDesignCodeChange={(designCode) => {
                  setSelectedDesignCode(designCode)
                }} />
              ) : null
            )}
          </FormDataConsumer>

          {!isCustLogin &&
            <ReferenceInput
              source="customerId"
              reference="customers"
              perPage={1000}
              sort={{ field: "customerName", order: "ASC" }}
            >
              <AutocompleteInput
                optionText={(record: any) => {
                  if (!record) return '';
                  return `${record.customerName || ''} (${record.mobileNo || ''})`;
                }}
                fullWidth
                validate={required()}
              />
            </ReferenceInput>
          }

          <CustomerFromMobile />

          <FormDataConsumer>
            {({ formData }) => {
              const hasMobile = formData?.customerMobile && String(formData.customerMobile).trim().length === 10;
              const isNewFromMobile = !!formData?.isNewCustomerFromMobile;
              if (!hasMobile || !isNewFromMobile) return null;

              return (
                <div style={{ marginTop: 8, padding: 8, border: '1px solid #eee', borderRadius: 4 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>New Customer Details</div>
                  <TextInput source="newCustomerName" label="Customer Name" fullWidth variant="outlined"/>
                  <TextInput source="newCustomerCode" label="Customer Code" fullWidth variant="outlined"/>
                  <TextInput source="newCustomerState" label="State" fullWidth variant="outlined"/>
                  <TextInput source="newCustomerAddress1" label="Address 1" fullWidth variant="outlined"/>
                  <TextInput source="newCustomerAddress2" label="Address 2" fullWidth variant="outlined"/>
                  <TextInput source="newCustomerAddress3" label="Address 3" fullWidth variant="outlined"/>
                  <TextInput source="newCustomerPincode" label="Pincode" fullWidth variant="outlined"/>
                </div>
              );
            }}
          </FormDataConsumer>

          <FormDataConsumer>
            {({ formData }) => (
              formData?.customerId ? (
                <CustomerPreview id={formData.customerId} dataProvider={dataProvider} />
              ) : null
            )}
          </FormDataConsumer>

          <FormDataConsumer>
            {({ formData }) => (
              <OrderItemsGrid
                designCode={formData.designCode}
                onChange={handleItemsChange}
                selectedDesignCode={selectedDesignCode}
              />
            )}
          </FormDataConsumer>
        </SimpleForm>
      </Create>
    </React.Fragment>
  );
};

const DesignPreview = ({ id, dataProvider, onDesignCodeChange }: { id: any; dataProvider: any; onDesignCodeChange: (designCode: string) => void }) => {
  const [rec, setRec] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    if (!id) return;
    dataProvider
      .getOne('product-groups', { id })
      .then((r: any) => {
        if (mounted) {
          setRec(r.data);
          onDesignCodeChange(r.data.designCode);
        }
      })
      .catch(() => {
        if (mounted) setRec(null);
      });
    return () => {
      mounted = false;
    };
  }, [id, dataProvider]);

  if (!rec) return null;
  const src = rec.photograph || '';
  if (!src) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
      <img src={src} alt={rec.designName || rec.name} style={{ width: 72, height: 72, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
      <div style={{ marginLeft: 8 }}>{rec.designName || rec.name}</div>
    </div>
  );
};

const CustomerPreview = ({ id, dataProvider }: { id: any; dataProvider: any }) => {
  const [rec, setRec] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    if (!id) return;
    dataProvider
      .getOne('customers', { id })
      .then((r: any) => {
        if (mounted) setRec(r.data);
      })
      .catch(() => {
        if (mounted) setRec(null);
      });
    return () => {
      mounted = false;
    };
  }, [id, dataProvider]);

  if (!rec) return null;
  const fields = [
    { label: 'Mobile', key: 'mobileNo' },
    { label: 'Phone', key: 'phone' },
    { label: 'Address', key: 'address' },
    { label: 'Address 1', key: 'address1' },
    { label: 'Address 2', key: 'address2' },
    { label: 'City', key: 'city' },
    { label: 'District', key: 'district' },
    { label: 'State', key: 'state' },
    { label: 'Postal', key: 'postalCode' },
    { label: 'Country', key: 'country' },
  ];
  return (
    <div style={{ marginTop: 8, padding: 8, border: '1px solid #eee', borderRadius: 4 }}>
      <div style={{ fontWeight: 600 }}>{rec.customerName || rec.userName}</div>
      <div style={{ marginTop: 6 }}>
        {fields.map((f) => {
          const v = rec[f.key];
          return v ? (
            <div key={f.key} style={{ color: '#444', fontSize: 13 }}>
              <strong style={{ marginRight: 6 }}>{f.label}:</strong> {v}
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
};